import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLocale } from '../../utils/LocaleContext';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const ADMIN_ROLES = ['admin', 'systemAdmin', 'orgAdmin'];

const toSummaryDisplay = r => ({
  id: r.brokerId,
  name: r.brokerName,
  manager: r.manager || '—',
  policies: r.activePolicies || 0,
  productMix: r.productMix || [],
  recurring: r.recurring || 0,
  acquisition: r.acquisition || 0,
  earned: r.totalEarned || 0,
  paid: r.totalPaid || 0,
  outstanding: r.outstanding || 0,
});

const currentQuarter = (d = new Date()) => {
  const q = Math.floor(d.getUTCMonth() / 3) + 1;
  return `${d.getUTCFullYear()}-Q${q}`;
};

const buildPeriodOptions = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const currentQ = Math.floor(now.getUTCMonth() / 3) + 1;
  const opts = [];
  for (let q = currentQ; q >= 1; q -= 1) opts.push({ value: `${year}-Q${q}`, label: `Q${q} ${year}` });
  for (let q = 4; q >= 1; q -= 1) opts.push({ value: `${year - 1}-Q${q}`, label: `Q${q} ${year - 1}` });
  return opts;
};

// Resolve user's role from any of the shapes seen across the app.
const resolveUserRole = (loggedinUser) => {
  const info = loggedinUser?.userInfo;
  return (
    info?.data?.data?.role
    || info?.data?.user?.role
    || info?.data?.role
    || info?.user?.role
    || info?.role
    || null
  );
};

const useCommissionsFunctions = () => {
  useLocale();
  const { t } = useTranslation();
  const loggedinUser = useSelector(state => state.loggedinUser);
  const userRole = resolveUserRole(loggedinUser);
  const isAdmin = ADMIN_ROLES.includes(userRole);

  const [period, setPeriod] = useState(currentQuarter());
  const periodOptions = useMemo(buildPeriodOptions, []);

  const [brokers, setBrokers] = useState([]);
  // Portfolio premium + commission breakdown for the period. Null until it
  // loads, and left null on failure so the panel simply does not render —
  // a commission screen that works must not go blank over an extra figure.
  const [portfolio, setPortfolio] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [showGenerate, setShowGenerate] = useState(false);

  const [viewMode, setViewMode] = useState('list');
  const [statementBrokerId, setStatementBrokerId] = useState(null);
  const [linkingPolicies, setLinkingPolicies] = useState(false);

  const TABS = [
    { id: 'overview', label: t('screens.commissions.tabOverview') },
    ...(isAdmin ? [{ id: 'plans', label: t('screens.commissions.tabPlans') }] : []),
    { id: 'sliding-scale', label: t('screens.commissions.tabSlidingScale') },
  ];

  const fetchSummary = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/commissions/summary?period=${encodeURIComponent(period)}`, { withCredentials: true });
      const rows = Array.isArray(resp.data?.data) ? resp.data.data.map(toSummaryDisplay) : [];
      setBrokers(rows);
    } catch {
      setBrokers([]);
    }
  }, [period]);

  const fetchPortfolio = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/commissions/portfolio?period=${encodeURIComponent(period)}`, { withCredentials: true });
      setPortfolio(resp.data?.data || null);
    } catch {
      setPortfolio(null);
    }
  }, [period]);

  const fetchPlans = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/commissions/plans`, { withCredentials: true });
      const rows = Array.isArray(resp.data?.data) ? resp.data.data : [];
      setPlans(rows);
    } catch {
      setPlans([]);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchSummary(), fetchPortfolio(), isAdmin ? fetchPlans() : Promise.resolve()]);
    setLoading(false);
  }, [fetchSummary, fetchPortfolio, fetchPlans, isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Deep-link support: /commissions?broker=<id>&period=<p>
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const brokerParam = params.get('broker');
    const periodParam = params.get('period');
    if (periodParam) setPeriod(periodParam);
    if (brokerParam) {
      setStatementBrokerId(brokerParam);
      setViewMode('statement');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = brokers.filter(b => {
    const q = search.toLowerCase();
    return !q || b.name.toLowerCase().includes(q) || (b.manager || '').toLowerCase().includes(q);
  });

  const totalEarned = brokers.reduce((s, b) => s + (b.earned || 0), 0);
  const totalPaid = brokers.reduce((s, b) => s + (b.paid || 0), 0);
  const totalOutstanding = brokers.reduce((s, b) => s + (b.outstanding || 0), 0);

  const linkPolicies = useCallback(async () => {
    if (linkingPolicies) return;
    setLinkingPolicies(true);
    try {
      const resp = await axios.post(`${BASE_URL}/commissions/link-policies`, {}, { withCredentials: true });
      const d = resp.data?.data || {};
      const summary = t(
        'screens.commissions.overview.linkPoliciesResult',
        {
          defaultValue: '{{brokerLinked}} broker link(s), {{productInferred}} product infer(s), {{skipped}} skipped',
          brokerLinked: d.brokerLinked ?? 0,
          productInferred: d.productInferred ?? 0,
          skipped: d.skipped ?? 0,
        },
      );
      toast.success(summary, { duration: 5000 });
      await fetchSummary();
      await fetchPortfolio();
    } catch (err) {
      const msg = err.response?.data?.message
        || t('screens.commissions.overview.linkPoliciesFailed', { defaultValue: 'Failed to link policies.' });
      toast.error(msg);
    } finally {
      setLinkingPolicies(false);
    }
  }, [linkingPolicies, fetchSummary, fetchPortfolio, t]);

  const openStatement = (brokerId) => {
    setStatementBrokerId(brokerId);
    setViewMode('statement');
  };
  const closeStatement = () => {
    setStatementBrokerId(null);
    setViewMode('list');
    fetchAll();
  };

  return {
    userRole,
    isAdmin,
    brokers,
    plans,
    setPlans,
    loading,
    activeTab,
    setActiveTab,
    search,
    setSearch,
    showGenerate,
    setShowGenerate,
    period,
    setPeriod,
    periodOptions,
    TABS,
    fetchCommissions: fetchAll,
    fetchSummary,
    portfolio,
    fetchPortfolio,
    fetchPlans,
    filtered,
    totalEarned,
    totalPaid,
    totalOutstanding,
    viewMode,
    statementBrokerId,
    openStatement,
    closeStatement,
    linkingPolicies,
    linkPolicies,
  };
};

export default useCommissionsFunctions;
